import React, { useState, useRef, useEffect } from 'react';
import {
	Flex,
	Tooltip,
	Text,
	Button,
	useToast,
	Box,
	AlertDialog,
	AlertDialogBody,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogContent,
	AlertDialogOverlay,
} from '@chakra-ui/react';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import c from '../../constant';
import DownloadAndViewButton from '../Files/DownloadAndViewButton';
import { faMinusSquare } from '@fortawesome/free-solid-svg-icons';
import './style.css';
const maxFileSize = 10485760 * 10;

const ADD_REPLY_LETTER_FILE_MUTATION = gql`
	mutation add($attachmentFileSize: Float!, $attachmentFile: Upload!, $id: String!) {
		complaintOnInsranceAddUserReplyLetterById(attachmentFile: $attachmentFile, attachmentFileSize: $attachmentFileSize, id: $id)
	}
`;

const MUTATION_REMOVE_REPLY_LETTER_FILE = gql`
	mutation rm($id: String!) {
		complaintOnInsuranceRemovePlaintiffReplyLetterFileById(id: $id)
	}
`;

export default function UserReplyLetterFile({ id, callback, existingReplyLetterFile, assignerNames }) {
	const { t, i18n } = useTranslation();
	const currentLang = i18n.language;
	const toast = useToast();
	const fileInput = useRef(null);

	const [attachmentFile, setAttachmentFile] = useState(null);

	const [addReplyLetterFile, { loading, error, data }] = useMutation(ADD_REPLY_LETTER_FILE_MUTATION);

	const [isOpen, setIsOpen] = React.useState(false);
	const onClose = () => setIsOpen(false);
	const cancelRef = React.useRef();

	const [removeLetter, { error: errorRemoveLetter, loading: loadingRemoveLetter, data: dataRemoveLetter }] = useMutation(MUTATION_REMOVE_REPLY_LETTER_FILE);

	useEffect(() => {
		if (dataRemoveLetter?.complaintOnInsuranceRemovePlaintiffReplyLetterFileById) {
			toast({
				title: t('Reply Letter file removed successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
			callback();
		}
	}, [dataRemoveLetter]);

	useEffect(() => {
		if (errorRemoveLetter) {
			errorRemoveLetter.graphQLErrors.map(({ message }, i) =>
				toast({
					title: t(message),
					status: 'error',
					isClosable: true,
					position: 'top-right',
				})
			);
		}
	}, [errorRemoveLetter]);

	useEffect(() => {
		if (error) {
			error.graphQLErrors.map(({ message }, i) =>
				toast({
					title: t(message),
					status: 'error',
					isClosable: true,
					position: 'top-right',
				})
			);
		}
	}, [error]);

	useEffect(() => {
		if (data?.complaintOnInsranceAddUserReplyLetterById) {
			toast({
				title: t('Reply Letter file uploaded successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
			callback();
		}
	}, [data]);

	const handleClickFile = () => {
		fileInput.current.click();
	};

	const handleFileChange = (event) => {
		var fileInput = false;
		if (event.target.files[0]) {
			fileInput = true;
		}
		if (fileInput) {
			const file = event.target.files[0];
			if (file.size > maxFileSize) {
				toast({
					title: t('File size over limit'),
					status: 'warning',
					isClosable: true,
					position: 'top-right',
				});
			} else {
				setAttachmentFile(event.target.files[0]);
			}
		}
	};

	function submitComment() {
		if (attachmentFile) {
			const variables = {
				attachmentFile: attachmentFile,
				attachmentFileSize: attachmentFile?.size,
				id: id,
			};
			addReplyLetterFile({
				variables,
			});
		}
	}

	return (
		<>
			<Flex direction="row" alignItems="center" pl="2" className="hidden-print">
				{existingReplyLetterFile ? (
					<Flex>
						<Tooltip label={`${t('Upload By')} ${currentLang === 'kh' ? assignerNames?.fullNameKhmer : assignerNames?.fullName}`}>
							<Flex className="viewContainer" alignItems="center" mt="2">
								<DownloadAndViewButton url={existingReplyLetterFile?.file} size={existingReplyLetterFile?.fileSize} disableViewToolTip />
								<FontAwesomeIcon
									onClick={() => setIsOpen(true)}
									className="remove"
									icon={faMinusSquare}
									style={{
										fontSize: 18,
										display: 'none',
										position: 'absolute',
										zIndex: '100',
										marginLeft: 175,
										marginBottom: 30,
										color: 'red',
										backgroundColor: 'white',
										padding: 2,
										cursor: 'pointer',
									}}
								/>
							</Flex>
						</Tooltip>
					</Flex>
				) : (
					<Flex>
						<Tooltip label={t('Maximum file size 100MB')}>
							<Flex
								maxW={480}
								borderRadius={c.borderRadius}
								pr="2"
								pl="2"
								h="37px"
								bg="blue.400"
								cursor="pointer"
								alignItems="center"
								m="2"
								onClick={() => {
									if (!loading) {
										handleClickFile();
									}
								}}
							>
								<Text minW="122px" w="122px" color="white">
									{t('Choose Attachment')}
								</Text>
								<FontAwesomeIcon icon={faFile} color="white" style={{ fontSize: 18, marginLeft: 4 }} />
								{attachmentFile?.name && (
									<Text noOfLines={1} bg="gray.200" pl="2" pr="2" ml="2">
										{attachmentFile?.name}
									</Text>
								)}
							</Flex>
						</Tooltip>
						<input style={{ display: 'none' }} type="file" onChange={(e) => handleFileChange(e)} ref={fileInput} />
					</Flex>
				)}
				{attachmentFile?.name && !existingReplyLetterFile && (
					<Flex justifyContent="flex-end">
						<Button color="#3498DA" isLoading={loading} onClick={() => submitComment()}>
							{t('Submit')}
						</Button>
					</Flex>
				)}
			</Flex>

			<>
				<AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
					<AlertDialogOverlay>
						<AlertDialogContent>
							<AlertDialogHeader fontSize="lg" fontWeight="bold">
								{t('Delete reply letter file')}
							</AlertDialogHeader>

							<AlertDialogBody>{t("Are you sure? You can't undo this action afterwards")}</AlertDialogBody>

							<AlertDialogFooter>
								<Button ref={cancelRef} disabled={loadingRemoveLetter} onClick={onClose}>
									{t('Cancel')}
								</Button>
								<Button
									isLoading={loadingRemoveLetter}
									colorScheme="red"
									onClick={() => {
										removeLetter({ variables: { id } });
									}}
									ml={3}
								>
									{t('Delete')}
								</Button>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialogOverlay>
				</AlertDialog>
			</>
		</>
	);
}
